import React, {useMemo, useState, useEffect, useCallback} from "react"
import {Card, Col, Container, Form, Row, Alert, Button} from "react-bootstrap";
import {useTranslation} from "react-i18next";
import BreadCrumb from "../../../Common/BreadCrumb";
import DataTable from "../../../Common/DataTable";

import {usersList} from "../../../data";
import {mockStores} from "../../../data/stores";
import { CreateUser } from "../Components/CreateUser";
import { EditUser } from "../Components/EditUser";
import { AssignStores } from "../Components/AssignStores";
import {COLORS} from "../../../Common/constants/colors";
import {dashboardAPI} from "../../../services/api";

interface User {
    id: string;
    userId?: string;
    username?: string;
    email: string;
    firstName?: string;
    lastName?: string;
    memberName?: string;
    role: string;
    roles?: string[];
    active: boolean;
    mobile?: string;
    image?: string | null;
    memberImage?: string;
    registeredOn?: string;
    companyId?: string;
    storeIds?: string[];
    storeGroupIds?: string[];
}

const UserList = () => {
    const {t} = useTranslation();
    document.title = t('UserList.title') || 'Users';
    
    // Check if user is demo user
    const isDemoUser = useMemo(() => {
        const authUser = sessionStorage.getItem("authUser");
        if (!authUser) return false;
        try {
            const userData = JSON.parse(authUser);
            const token = userData.token || userData.access_token;
            return token?.startsWith('demo-token-') || userData.email === 'admin@demo.com.tr';
        } catch {
            return false;
        }
    }, []);

    const [data, setData] = useState<User[]>([]);
    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [assignModalOpen, setAssignModalOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState<User | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    // Handle window resize for mobile detection
    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Fetch users from backend for real users
    useEffect(() => {
        if (isDemoUser) {
            // Use mock data for demo users - map to User format
            // Add some demo store assignments and store groups
            
            // Map stores to get store IDs
            const storeIds = mockStores.map((s: any) => s.id);
            
            const mappedUsers: User[] = usersList.map((user: any, index: number) => {
                // Assign some stores to store managers (users 1-5)
                let userStoreIds: string[] = [];
                let userStoreGroupIds: string[] = [];
                
                if (user.role === 'store_manager') {
                    // Assign 1-3 stores to each store manager
                    const startIdx = (index - 1) * 2;
                    const endIdx = Math.min(startIdx + 2, storeIds.length);
                    userStoreIds = storeIds.slice(startIdx, endIdx);
                } else if (user.role === 'area_manager') {
                    // Assign store groups to area managers
                    if (index === 11) {
                        userStoreGroupIds = ['group-1'];
                    } else if (index === 12) {
                        userStoreGroupIds = ['group-2'];
                    }
                }
                
                return {
                    id: String(user.id),
                    userId: String(user.id),
                    username: user.email || '',
                    email: user.email || '',
                    firstName: user.memberName?.split(' ')[0] || '',
                    lastName: user.memberName?.split(' ').slice(1).join(' ') || '',
                    memberName: user.memberName || '',
                    role: user.role || '',
                    roles: user.role === 'admin' ? ['ROLE_COMPANY_ADMIN'] : 
                           user.role === 'store_manager' ? ['ROLE_STORE_MANAGER'] :
                           user.role === 'area_manager' ? ['ROLE_AREA_MANAGER'] : [],
                    active: user.active !== undefined ? user.active : (user.status !== 'Disabled'),
                    mobile: user.mobile || '',
                    image: user.memberImage || null,
                    registeredOn: user.registeredOn || new Date().toISOString(),
                    storeIds: userStoreIds,
                    storeGroupIds: userStoreGroupIds
                };
            });
            
            // Add a couple of Area Manager demo users
            mappedUsers.push({
                id: 'demo-area-1',
                userId: 'demo-area-1',
                username: 'area.manager1@demo.com.tr',
                email: 'area.manager1@demo.com.tr',
                firstName: 'Mehmet',
                lastName: 'Yılmaz',
                memberName: 'Mehmet Yılmaz',
                role: 'area_manager',
                roles: ['ROLE_AREA_MANAGER'],
                active: true,
                mobile: '+90 555 111 22 33',
                image: null,
                registeredOn: new Date().toISOString(),
                storeIds: [], // Will be resolved from store groups
                storeGroupIds: ['group-1', 'group-2']
            });
            
            mappedUsers.push({
                id: 'demo-area-2',
                userId: 'demo-area-2',
                username: 'area.manager2@demo.com.tr',
                email: 'area.manager2@demo.com.tr',
                firstName: 'Ayşe',
                lastName: 'Demir',
                memberName: 'Ayşe Demir',
                role: 'area_manager',
                roles: ['ROLE_AREA_MANAGER'],
                active: true,
                mobile: '+90 555 222 33 44',
                image: null,
                registeredOn: new Date().toISOString(),
                storeIds: [], // Will be resolved from store groups
                storeGroupIds: ['group-3']
            });
            
            setData(mappedUsers);
            return;
        }

        const fetchUsers = async () => {
            setIsLoading(true);
            setError(null);
            try {
                console.log('📋 Fetching users from backend...');
                const response: any = await dashboardAPI.getUsers();
                
                // Normalize response - handle both AxiosResponse and direct data
                const users = Array.isArray(response) ? response : (response?.data || []);
                
                console.log('✅ Users received:', users.length, 'users');
                
                // Map backend UserDTO to frontend User format
                const mappedUsers: User[] = users.map((user: any) => ({
                    id: user.userId || user.id || '',
                    userId: user.userId || user.id || '',
                    username: user.username || user.email || '',
                    email: user.email || '',
                    firstName: user.firstName || '',
                    lastName: user.lastName || '',
                    memberName: user.firstName && user.lastName 
                        ? `${user.firstName} ${user.lastName}` 
                        : user.username || user.email,
                    role: user.roles?.[0] || user.role || '',
                    roles: user.roles || [user.role].filter(Boolean),
                    active: user.active !== undefined ? user.active : true,
                    mobile: user.mobile || '',
                    image: user.image || null,
                    registeredOn: user.createdOn || new Date().toISOString(),
                    companyId: user.companyId,
                    storeIds: user.storeIds || [],
                    storeGroupIds: user.storeGroupIds || []
                }));
                
                setData(mappedUsers);
            } catch (error: any) {
                console.error('❌ Failed to fetch users:', error);
                setError(error?.response?.data?.message || error?.message || 'Failed to fetch users');
                setData([]);
            } finally {
                setIsLoading(false);
            }
        };

        fetchUsers();
    }, [isDemoUser]);

    const handleCreate = () => {
        setSelectedUser(null);
        setCreateModalOpen(true);
    };

    const handleEdit = (user: User) => {
        setSelectedUser(user);
        setEditModalOpen(true);
    };

    const handleAssign = (user: User) => {
        setSelectedUser(user);
        setAssignModalOpen(true);
    };

    const handleToggleActive = useCallback(async (user: User, newActiveState: boolean) => {
        if (isDemoUser) {
            // For demo, just update local state
            const newData = [...data];
            const index = newData.findIndex(u => u.id === user.id);
            if (index !== -1) {
                newData[index].active = newActiveState;
                setData(newData);
            }
            return;
        }

        try {
            // Optimistically update UI
            const newData = [...data];
            const index = newData.findIndex(u => u.id === user.id);
            if (index !== -1) {
                newData[index].active = newActiveState;
                setData(newData);
            }

            // Update on backend
            await dashboardAPI.updateUser(user.userId || user.id, {
                active: newActiveState
            });
        } catch (error: any) {
            console.error('Failed to update user active status:', error);
            // Revert on error
            const newData = [...data];
            const index = newData.findIndex(u => u.id === user.id);
            if (index !== -1) {
                newData[index].active = !newActiveState;
                setData(newData);
            }
            alert(error?.response?.data?.message || error?.message || 'Failed to update user status');
        }
    }, [isDemoUser, data]);

    const handleDeactivate = useCallback(async (user: User) => {
        if (!window.confirm(t('UserList.confirm_deactivate') || `Are you sure you want to deactivate ${user.memberName || user.email}?`)) {
            return;
        }

        if (isDemoUser) {
            // For demo, just update local state
            const newData = [...data];
            const index = newData.findIndex(u => u.id === user.id);
            if (index !== -1) {
                newData[index].active = false;
                setData(newData);
            }
            return;
        }

        try {
            await dashboardAPI.deactivateUser(user.userId || user.id);
            
            // Update local state
            const newData = [...data];
            const index = newData.findIndex(u => u.id === user.id);
            if (index !== -1) {
                newData[index].active = false;
                setData(newData);
            }
        } catch (error: any) {
            console.error('Failed to deactivate user:', error);
            alert(error?.response?.data?.message || error?.message || 'Failed to deactivate user');
        }
    }, [isDemoUser, data, t]);

    const getRoleDisplayName = useCallback((role: string) => {
        const roleMap: { [key: string]: string } = {
            'ROLE_GENERAL_ADMIN': t('UserList.role_general_admin') || 'General Admin',
            'ROLE_COMPANY_ADMIN': t('UserList.role_company_admin') || 'Company Admin',
            'ROLE_STORE_MANAGER': t('UserList.role_store_manager') || 'Store Manager',
            'ROLE_AREA_MANAGER': t('UserList.role_area_manager') || 'Area Manager',
            'admin': t('UserList.role_admin') || 'Admin',
            'store_manager': t('UserList.role_store_manager') || 'Store Manager',
            'area_manager': t('UserList.role_area_manager') || 'Area Manager'
        };
        return roleMap[role] || role;
    }, [t]);

    const columns = useMemo(
        () => {
            const cols: any[] = [
                {
                    Header: t('UserList.name') || 'Name',
                    accessor: "memberName",
                    Cell: ({row, value}: any) => {
                        const user = data[row.index];
                        return (
                            <div className="d-flex align-items-center">
                                {user.image || user.memberImage ? (
                                    <img 
                                        src={user.image || user.memberImage} 
                                        alt={value} 
                                        className="rounded-circle me-2"
                                        style={{width: '32px', height: '32px', objectFit: 'cover', flexShrink: 0}}
                                    />
                                ) : (
                                    <div 
                                        className="rounded-circle me-2 d-flex align-items-center justify-content-center"
                                        style={{
                                            width: '32px', 
                                            height: '32px', 
                                            backgroundColor: COLORS.PRIMARY_COLOR,
                                            color: 'white',
                                            fontSize: '14px',
                                            fontWeight: 'bold',
                                            flexShrink: 0
                                        }}
                                    >
                                        {(user.firstName?.[0] || user.email?.[0] || 'U').toUpperCase()}
                                    </div>
                                )}
                                <div style={{minWidth: 0, flex: 1}}>
                                    <div style={{wordBreak: 'break-word'}}>{value || user.email}</div>
                                    {!isMobile && user.email && user.email !== value && (
                                        <small className="text-muted" style={{wordBreak: 'break-word'}}>{user.email}</small>
                                    )}
                                </div>
                            </div>
                        );
                    }
                },
                {
                    Header: t('UserList.role') || 'Role',
                    accessor: "role",
                    Cell: ({value}: any) => (
                        <div>{getRoleDisplayName(value)}</div>
                    )
                },
            ];
            
            // Add email and mobile columns only on desktop
            if (!isMobile) {
                cols.push(
                    {
                        Header: t('UserList.email') || 'Email',
                        accessor: "email",
                        Cell: ({value}: any) => (
                            <div style={{wordBreak: 'break-word'}}>
                                {value}
                            </div>
                        )
                    },
                    {
                        Header: t('UserList.mobile') || 'Mobile',
                        accessor: "mobile",
                        Cell: ({value}: any) => value || '-'
                    }
                );
            }
            
            cols.push(
                {
                    Header: t('UserList.active') || 'Active',
                    accessor: "active",
                    Cell: ({row, value}: any) => {
                        const user = data[row.index];
                        return (
                            <div className="form-check form-switch form-switch-custom form-switch-primary">
                                <Form.Check 
                                    onChange={() => handleToggleActive(user, !value)} 
                                    type="checkbox" 
                                    role="switch" 
                                    id={`user-switch-${user.id}`} 
                                    checked={value}
                                    disabled={isLoading}
                                />
                            </div>
                        );
                    },
                },
                {
                    Header: t('UserList.actions') || 'Actions',
                    accessor: "actions",
                    Cell: ({row}: any) => {
                        const user = data[row.index];
                        return (
                            <div className={`d-flex ${isMobile ? 'flex-column gap-1' : 'gap-2'}`}>
                                <Button
                                    variant="outline-primary"
                                    size="sm"
                                    onClick={() => handleEdit(user)}
                                    className={isMobile ? "w-100" : ""}
                                >
                                    {t('UserList.edit') || 'Edit'}
                                </Button>
                                {(user.role === 'ROLE_STORE_MANAGER' || user.role === 'store_manager') && (
                                    <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        onClick={() => handleAssign(user)}
                                        className={isMobile ? "w-100" : ""}
                                    >
                                        {t('UserList.assign_stores') || 'Assign Stores'}
                                    </Button>
                                )}
                                {(user.role === 'ROLE_AREA_MANAGER' || user.role === 'area_manager') && (
                                    <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        onClick={() => handleAssign(user)}
                                        className={isMobile ? "w-100" : ""}
                                    >
                                        {t('UserList.assign_store_groups') || 'Assign Store Groups'}
                                    </Button>
                                )}
                                {user.active && (
                                    <Button
                                        variant="outline-danger"
                                        size="sm"
                                        onClick={() => handleDeactivate(user)}
                                        className={isMobile ? "w-100" : ""}
                                    >
                                        {t('UserList.deactivate') || 'Deactivate'}
                                    </Button>
                                )}
                            </div>
                        );
                    },
                }
            );
            
            return cols;
        },
        [data, t, isLoading, getRoleDisplayName, handleDeactivate, handleToggleActive, isMobile]
    );

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <BreadCrumb
                        items={[
                            {label: t('Dashboard.header') || 'Dashboard', path: "/"},
                            {label: t('UserList.page_title') || 'Users', path: "/users"}
                        ]}
                    />
                    {error && (
                        <Row>
                            <Col>
                                <Alert variant="danger" onClose={() => setError(null)} dismissible>
                                    {error}
                                </Alert>
                            </Col>
                        </Row>
                    )}
                    <Row>
                        <Col xl={12}>
                            <Card>
                                <Card.Header className={`d-flex justify-content-between align-items-center ${isMobile ? 'flex-column gap-2' : ''}`}>
                                    <h5 className={`card-title mb-0 ${isMobile ? 'w-100 text-center' : ''}`}>
                                        {t('UserList.page_title') || 'Users'}
                                    </h5>
                                    <Button 
                                        variant="primary" 
                                        onClick={handleCreate}
                                        className={isMobile ? 'w-100' : ''}
                                        size={isMobile ? 'sm' : undefined}
                                    >
                                        {t('UserList.create_user') || 'Create User'}
                                    </Button>
                                </Card.Header>
                                <Card.Body>
                                    {isLoading ? (
                                        <div className="text-center py-4">
                                            <div className="spinner-border text-primary" role="status">
                                                <span className="visually-hidden">Loading...</span>
                                            </div>
                                        </div>
                                    ) : isMobile ? (
                                        // Mobile card view
                                        <div className="d-flex flex-column gap-3">
                                            {data.length === 0 ? (
                                                <div className="text-center py-5 text-muted">
                                                    {t('UserList.no_users') || 'No users found'}
                                                </div>
                                            ) : (
                                                data.map((user) => (
                                                    <Card key={user.id} className="shadow-sm">
                                                        <Card.Body>
                                                            <div className="d-flex align-items-start gap-3 mb-3">
                                                                {user.image || user.memberImage ? (
                                                                    <img 
                                                                        src={user.image || user.memberImage} 
                                                                        alt={user.memberName || user.email} 
                                                                        className="rounded-circle"
                                                                        style={{width: '48px', height: '48px', objectFit: 'cover', flexShrink: 0}}
                                                                    />
                                                                ) : (
                                                                    <div 
                                                                        className="rounded-circle d-flex align-items-center justify-content-center"
                                                                        style={{
                                                                            width: '48px', 
                                                                            height: '48px', 
                                                                            backgroundColor: COLORS.PRIMARY_COLOR,
                                                                            color: 'white',
                                                                            fontSize: '18px',
                                                                            fontWeight: 'bold',
                                                                            flexShrink: 0
                                                                        }}
                                                                    >
                                                                        {(user.firstName?.[0] || user.email?.[0] || 'U').toUpperCase()}
                                                                    </div>
                                                                )}
                                                                <div className="flex-grow-1" style={{minWidth: 0}}>
                                                                    <h6 className="mb-1" style={{wordBreak: 'break-word'}}>
                                                                        {user.memberName || user.email}
                                                                    </h6>
                                                                    <small className="text-muted d-block mb-1" style={{wordBreak: 'break-word'}}>
                                                                        {user.email}
                                                                    </small>
                                                                    <div className="d-flex flex-wrap gap-2 align-items-center mt-2">
                                                                        <span className="badge bg-primary">
                                                                            {getRoleDisplayName(user.role)}
                                                                        </span>
                                                                        <div className="form-check form-switch form-switch-custom form-switch-primary">
                                                                            <Form.Check 
                                                                                onChange={() => handleToggleActive(user, !user.active)} 
                                                                                type="checkbox" 
                                                                                role="switch" 
                                                                                id={`user-switch-mobile-${user.id}`} 
                                                                                checked={user.active}
                                                                                disabled={isLoading}
                                                                                label={<small>{t('UserList.active') || 'Active'}</small>}
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                    {user.mobile && (
                                                                        <small className="text-muted d-block mt-1">
                                                                            📱 {user.mobile}
                                                                        </small>
                                                                    )}
                                                                </div>
                                                            </div>
                                                            <div className="d-flex flex-column gap-2 mt-3 pt-3 border-top">
                                                                <Button
                                                                    variant="outline-primary"
                                                                    size="sm"
                                                                    onClick={() => handleEdit(user)}
                                                                    className="w-100"
                                                                >
                                                                    {t('UserList.edit') || 'Edit'}
                                                                </Button>
                                                                {(user.role === 'ROLE_STORE_MANAGER' || user.role === 'store_manager') && (
                                                                    <Button
                                                                        variant="outline-secondary"
                                                                        size="sm"
                                                                        onClick={() => handleAssign(user)}
                                                                        className="w-100"
                                                                    >
                                                                        {t('UserList.assign_stores') || 'Assign Stores'}
                                                                    </Button>
                                                                )}
                                                                {(user.role === 'ROLE_AREA_MANAGER' || user.role === 'area_manager') && (
                                                                    <Button
                                                                        variant="outline-secondary"
                                                                        size="sm"
                                                                        onClick={() => handleAssign(user)}
                                                                        className="w-100"
                                                                    >
                                                                        {t('UserList.assign_store_groups') || 'Assign Store Groups'}
                                                                    </Button>
                                                                )}
                                                                {user.active && (
                                                                    <Button
                                                                        variant="outline-danger"
                                                                        size="sm"
                                                                        onClick={() => handleDeactivate(user)}
                                                                        className="w-100"
                                                                    >
                                                                        {t('UserList.deactivate') || 'Deactivate'}
                                                                    </Button>
                                                                )}
                                                            </div>
                                                        </Card.Body>
                                                    </Card>
                                                ))
                                            )}
                                        </div>
                                    ) : (
                                        // Desktop table view
                                        <div className="table-responsive">
                                            <DataTable
                                                columns={columns}
                                                data={data}
                                                title={t('UserList.search_user') || 'Search users...'}
                                                editable={false}
                                                deletable={false}
                                                alertsDeletable={false}
                                            />
                                        </div>
                                    )}
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                    <CreateUser
                        show={createModalOpen}
                        onClose={() => setCreateModalOpen(false)}
                        onSave={async (newUser: any) => {
                            if (isDemoUser) {
                                // For demo, just add to local state
                                const user: User = {
                                    id: `demo-user-${Date.now()}`,
                                    email: newUser.email,
                                    username: newUser.username,
                                    firstName: newUser.firstName,
                                    lastName: newUser.lastName,
                                    memberName: `${newUser.firstName} ${newUser.lastName}`,
                                    role: newUser.role,
                                    active: true,
                                    mobile: newUser.mobile
                                };
                                setData([...data, user]);
                                setCreateModalOpen(false);
                                return;
                            }

                            try {
                                // Create on backend
                                await dashboardAPI.createUser(newUser);
                                
                                // Refresh users list
                                const response: any = await dashboardAPI.getUsers();
                                const users = Array.isArray(response) ? response : (response?.data || []);
                                const mappedUsers: User[] = users.map((u: any) => ({
                                    id: u.userId || u.id || '',
                                    userId: u.userId || u.id || '',
                                    username: u.username || u.email || '',
                                    email: u.email || '',
                                    firstName: u.firstName || '',
                                    lastName: u.lastName || '',
                                    memberName: u.firstName && u.lastName 
                                        ? `${u.firstName} ${u.lastName}` 
                                        : u.username || u.email,
                                    role: u.roles?.[0] || u.role || '',
                                    roles: u.roles || [u.role].filter(Boolean),
                                    active: u.active !== undefined ? u.active : true,
                                    mobile: u.mobile || '',
                                    image: u.image || null,
                                    registeredOn: u.createdOn || new Date().toISOString(),
                                    companyId: u.companyId,
                                    storeIds: u.storeIds || [],
                                    storeGroupIds: u.storeGroupIds || []
                                }));
                                setData(mappedUsers);
                                setCreateModalOpen(false);
                            } catch (error: any) {
                                console.error('Failed to create user:', error);
                                alert(error?.response?.data?.message || error?.message || 'Failed to create user');
                            }
                        }}
                    />
                    <EditUser
                        show={editModalOpen}
                        user={selectedUser}
                        onClose={() => setEditModalOpen(false)}
                        onSave={async (updatedUser: any) => {
                            if (isDemoUser) {
                                // For demo, just update local state
                                const newData = [...data];
                                const index = newData.findIndex(u => u.id === updatedUser.id);
                                if (index !== -1) {
                                    const updated = { ...newData[index], ...updatedUser };
                                    // Update roles array if role changed
                                    if (updatedUser.role) {
                                        updated.roles = updatedUser.role === 'ROLE_COMPANY_ADMIN' ? ['ROLE_COMPANY_ADMIN'] :
                                                       updatedUser.role === 'ROLE_STORE_MANAGER' ? ['ROLE_STORE_MANAGER'] :
                                                       updatedUser.role === 'ROLE_AREA_MANAGER' ? ['ROLE_AREA_MANAGER'] : [];
                                    }
                                    newData[index] = updated;
                                    setData(newData);
                                }
                                setEditModalOpen(false);
                                return;
                            }

                            try {
                                // Update on backend
                                await dashboardAPI.updateUser(updatedUser.userId || updatedUser.id, updatedUser);
                                
                                // Refresh users list
                                const response: any = await dashboardAPI.getUsers();
                                const users = Array.isArray(response) ? response : (response?.data || []);
                                const mappedUsers: User[] = users.map((u: any) => ({
                                    id: u.userId || u.id || '',
                                    userId: u.userId || u.id || '',
                                    username: u.username || u.email || '',
                                    email: u.email || '',
                                    firstName: u.firstName || '',
                                    lastName: u.lastName || '',
                                    memberName: u.firstName && u.lastName 
                                        ? `${u.firstName} ${u.lastName}` 
                                        : u.username || u.email,
                                    role: u.roles?.[0] || u.role || '',
                                    roles: u.roles || [u.role].filter(Boolean),
                                    active: u.active !== undefined ? u.active : true,
                                    mobile: u.mobile || '',
                                    image: u.image || null,
                                    registeredOn: u.createdOn || new Date().toISOString(),
                                    companyId: u.companyId,
                                    storeIds: u.storeIds || [],
                                    storeGroupIds: u.storeGroupIds || []
                                }));
                                setData(mappedUsers);
                                setEditModalOpen(false);
                            } catch (error: any) {
                                console.error('Failed to update user:', error);
                                alert(error?.response?.data?.message || error?.message || 'Failed to update user');
                            }
                        }}
                    />
                    <AssignStores
                        show={assignModalOpen}
                        user={selectedUser}
                        onClose={() => setAssignModalOpen(false)}
                        onSave={async (assignmentData: any) => {
                            if (isDemoUser) {
                                // For demo, update local state
                                const newData = [...data];
                                const index = newData.findIndex(u => u.id === selectedUser?.id);
                                if (index !== -1) {
                                    if (selectedUser?.role === 'ROLE_STORE_MANAGER' || selectedUser?.role === 'store_manager') {
                                        newData[index].storeIds = assignmentData.storeIds || [];
                                    } else if (selectedUser?.role === 'ROLE_AREA_MANAGER' || selectedUser?.role === 'area_manager') {
                                        newData[index].storeGroupIds = assignmentData.storeGroupIds || [];
                                        // Resolve store groups to stores for demo
                                        const allStoreIds: string[] = [];
                                        assignmentData.storeGroupIds?.forEach((groupId: string) => {
                                            // For demo, assign stores based on group
                                            if (groupId === 'group-1') {
                                                allStoreIds.push(...mockStores.slice(0, 5).map((s: any) => s.id));
                                            } else if (groupId === 'group-2') {
                                                allStoreIds.push(...mockStores.slice(5, 10).map((s: any) => s.id));
                                            } else if (groupId === 'group-3') {
                                                allStoreIds.push(...mockStores.slice(10, 15).map((s: any) => s.id));
                                            }
                                        });
                                        newData[index].storeIds = Array.from(new Set(allStoreIds)); // Remove duplicates
                                    }
                                    setData(newData);
                                }
                                setAssignModalOpen(false);
                                return;
                            }

                            try {
                                if (selectedUser?.role === 'ROLE_STORE_MANAGER' || selectedUser?.role === 'store_manager') {
                                    await dashboardAPI.assignStoresToUser(selectedUser.userId || selectedUser.id, assignmentData.storeIds);
                                } else if (selectedUser?.role === 'ROLE_AREA_MANAGER' || selectedUser?.role === 'area_manager') {
                                    await dashboardAPI.assignStoreGroupsToUser(selectedUser.userId || selectedUser.id, assignmentData.storeGroupIds);
                                }
                                
                                // Refresh users list
                                const response: any = await dashboardAPI.getUsers();
                                const users = Array.isArray(response) ? response : (response?.data || []);
                                const mappedUsers: User[] = users.map((u: any) => ({
                                    id: u.userId || u.id || '',
                                    userId: u.userId || u.id || '',
                                    username: u.username || u.email || '',
                                    email: u.email || '',
                                    firstName: u.firstName || '',
                                    lastName: u.lastName || '',
                                    memberName: u.firstName && u.lastName 
                                        ? `${u.firstName} ${u.lastName}` 
                                        : u.username || u.email,
                                    role: u.roles?.[0] || u.role || '',
                                    roles: u.roles || [u.role].filter(Boolean),
                                    active: u.active !== undefined ? u.active : true,
                                    mobile: u.mobile || '',
                                    image: u.image || null,
                                    registeredOn: u.createdOn || new Date().toISOString(),
                                    companyId: u.companyId,
                                    storeIds: u.storeIds || [],
                                    storeGroupIds: u.storeGroupIds || []
                                }));
                                setData(mappedUsers);
                                setAssignModalOpen(false);
                            } catch (error: any) {
                                console.error('Failed to assign stores/store groups:', error);
                                alert(error?.response?.data?.message || error?.message || 'Failed to assign stores/store groups');
                            }
                        }}
                    />
                </Container>
            </div>
        </React.Fragment>
    )

}

export default UserList;

